import { Injectable } from "@angular/core";
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { throwError, Observable } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { OAuthService } from 'angular-oauth2-oidc';
import { FCError } from '../common/models/fcerror';
import { FCToasterService } from '../common/toaster/FCToasterService';
import { LoggingService } from './logging.service';
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { AppConfigService } from './app-config.service';

@Injectable()
export class InterceptorService implements HttpInterceptor {
    constructor(
        private oauthService: OAuthService,
        private fcerror: FCError,
        private toastr: FCToasterService,
        private loggingService: LoggingService,
        private appConfigService: AppConfigService
    ) {}

    private serializeBody(body: any): string {
        if (body === null || body === undefined) return '';
        if (typeof body === 'string') return body.substring(0, 8192);
        if (body instanceof FormData) return '[FormData]';
        if (body instanceof ArrayBuffer || body instanceof Blob) return '[Binary]';
        try {
            return JSON.stringify(body).substring(0, 8192);
        } catch {
            return '[Unserializable]';
        }
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const startTime = Date.now();

        const claims = this.oauthService.getIdentityClaims();
        const userId: string = claims ? claims['oid'] : null;

        if (userId) {
            this.loggingService.setUserContext(userId);
        }

        const requestBody = this.serializeBody(req.body);

        const properties: { [key: string]: any } = {
            'http.method': req.method,
            'http.url': req.urlWithParams,
            ...(userId && { 'request-uid': userId }),
            ...(requestBody && { 'request.body': requestBody })
        };

        if (this.appConfigService.loaded) {
            this.loggingService.logTrace(`HTTP ${req.method} ${req.urlWithParams}`, properties);
        }

        return next.handle(req).pipe(
            tap((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse && this.appConfigService.loaded) {
                    const duration = Date.now() - startTime;
                    const responseBody = this.serializeBody(event.body);
                    this.loggingService.logHttpDependency(
                        req.method,
                        req.urlWithParams,
                        duration,
                        event.status,
                        event.status >= 200 && event.status < 300,
                        {
                            ...properties,
                            'http.status': event.status,
                            'http.duration_ms': duration,
                            ...(responseBody && { 'response.body': responseBody })
                        }
                    );
                }
            }),
            catchError((error: HttpErrorResponse) => {
                const duration = Date.now() - startTime;

                if (error.status === 401) {
                    sessionStorage.clear();
                    localStorage.clear();
                    this.oauthService.stopAutomaticRefresh();
                    this.oauthService.logOut(false);
                    window.location.href = this.appConfigService.logoutUrl;
                }

                if (this.appConfigService.loaded) {
                    const responseBody = this.serializeBody(error.error);
                    const errorProps: { [key: string]: any } = {
                        ...properties,
                        'http.status': error.status,
                        'http.statusText': error.statusText,
                        'http.duration_ms': duration,
                        'error.id': error.headers?.get('x-error-id') ?? '',
                        'correlation.id': error.headers?.get('x-correlation-id') ?? '',
                        ...(responseBody && { 'response.body': responseBody })
                    };
                    this.loggingService.logHttpDependency(req.method, req.urlWithParams, duration, error.status, false, errorProps);
                    this.loggingService.logException(error, SeverityLevel.Error);
                }

                return throwError(() => this.handleError(error));
            })
        );
    }

    private handleError(error: HttpErrorResponse): FCError {
        if (error.error instanceof ErrorEvent) {
            this.fcerror.message = `Error: ${error.error.message}`;
        } else if (error.error instanceof ProgressEvent || error.status === 0) {
            this.fcerror.code = 0;
            this.fcerror.status = 'Network Error';
            this.fcerror.error = '';
            this.fcerror.errorid = null;
            this.fcerror.trackingid = null;
            this.fcerror.message = 'Session expired or network connection lost. Please refresh the page and try again.';
        } else {
            this.fcerror.code = error.status;
            this.fcerror.status = error.statusText;
            this.fcerror.error = error.error ?? '';
            this.fcerror.errorid = error.headers?.get('x-error-id');
            this.fcerror.trackingid = error.headers?.get('x-correlation-id');
            const rawErrorDesc = this.fcerror.error?.['ErrorDescription'];
            const cleanErrorDesc = typeof rawErrorDesc === 'string'
                ? rawErrorDesc.replace(/^"+|"+$/g, '').trim()
                : undefined;
            const errorDetail = error.status === 500
                ? (cleanErrorDesc ?? 'FC : Backend error')
                : (cleanErrorDesc
                ?? this.fcerror.error?.['error']
                ?? this.fcerror.error?.['detail']
                ?? this.fcerror.error?.['description']
                ?? this.fcerror.error?.['message']
                ?? this.fcerror.error?.['title']
                ?? (typeof this.fcerror.error === 'string' && this.fcerror.error !== ''
                    ? this.fcerror.error
                    : (this.fcerror.error && typeof this.fcerror.error === 'object'
                        ? JSON.stringify(this.fcerror.error)
                        : this.fcerror.status)));
            this.fcerror.message = errorDetail;
        }

        if (this.fcerror?.message?.length > 0 && this.fcerror.code !== 401) {
            if (error.error === 'G3MS: API Call to G3MS failed' && error.url?.includes('unit/gateway')) {
                return error as any;
            }
            this.toastr.showError(this.fcerror.message);
        }
        return this.fcerror;
    }
}
